import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useAtom } from "jotai";
import "../sideMenu/style.scss";
import { useTranslation } from "react-i18next";
import EVCSUploadMethod from "./EVCSUploadMethod";
import { AccordionItem } from "../AccordianItem";
import { useForm } from "@mantine/form";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import displayError from "../../constants/utils";
import MGAAPI from "../../api/index";
import _ from "lodash";
import { addedStepsAtom } from "../../utils/atoms/addedStepsAtom";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import {
  useMantineColorScheme,
  Button,
  Card,
  Text,
  Stack,
  Grid,
  TextInput,
  Select,
} from "@mantine/core";
import {
  useAssessmentRouteParams,
  useCurrentDesignComponentLists,
  formatInputValue,
  mgaRegex,
  responsiveCol,
  useCurrentDesignVersion
} from "../../constants/helper";
import { parseISOToLocalDateAtMidnightUTC } from "../../utils/dateHelper";
import {
  toUtcISOStringWithStartTime,
  getUtcMidnightIso,
  getDefaultEvcsStartDate,
  formatProjectNumberRange,
} from "../../utils/helpers";
import { helpContextMenuAtom } from "../../utils/atoms/helpContextMenuAtom";
import { helpAtom } from "../../utils/atoms/helpAtom";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";
import { getErrorMessage } from "../../utils/errorUtils";
import { SeIcon } from "@se/web-ui-react";
import FleetTabs from "./FleetTabs";
import Fleet, {
  getFleetValidationErrors,
  getNormalizedTripProfile,
  getStoredOrGeneratedProfile,
  validateFleet,
} from "./FleetTabs/Fleet";
import { IconCaretDownFilled } from "@tabler/icons-react";
import AddAsset from "../Library/AddAsset.js";
import useAssetManagement from "../../hooks/useAssetManagement.js";
import SEConfirmModal from "../common/SEConfirmModal";
import ProjectNumberInput from "../common/ProjectNumberInput";

const MGA_API = new MGAAPI();

// arrivalProfile is always 24h in fixed 15-min slots, so x is implicit; the
// backend expects it as a flat single-dimension array of values, not [x, y]
// tuples or {x, y} objects (which is what the chart-editing UI stores).
const toPointArray = (profile) =>
  Array.isArray(profile)
    ? profile.map((point) => {
        if (Array.isArray(point)) return Number(point[1]) || 0;
        if (point && typeof point === "object") return Number(point.y) || 0;
        return Number(point) || 0;
      })
    : profile;

const validateRangedInput = (label, min, max, regex, message) => (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return message.required;
  }
  const stringValue = String(value).trim();
  const numberValue = Number(stringValue);
  return regex.test(stringValue) && numberValue >= min && numberValue <= max
    ? null
    : message.range;
};

const EVCSMethod = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const evcsRange = (field, min, max, regex, integer = false) =>
    validateRangedInput(
      t(`evcs.power.${field}`),
      min,
      max,
      regex,
      {
        required: t(`evcs.validation.${field}Required`),
        range: t(integer ? "evcs.validation.integerRange" : "evcs.validation.range", {
          field: t(`evcs.power.${field}`),
          ...formatProjectNumberRange(min, max, projectDetails?.projectDTO?.numberFormat),
        }),
      },
    );
  const {
    evcsComponents = [],
    projectDetails,
    initialTab = "",
    initialSelectedBuildingType,
    saveComponents,
    setLoadingComp,
  } = props;

    const selectedVersion = useCurrentDesignVersion(projectDetails?.designWithVersionsDTOList)

  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const [, setComponentStatus] = useAtom(componentStatusAtom);
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    projectDetails?.designComponentsList
  );

  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const [componentDetails, setComponentDetails] = useAtom(componentsAtom);
  const [openSteps, setOpenSteps] = useState(false);
  const [dataSource, setDataSource] = useState(initialTab || "load");
  const [distributions, setDistributions] = useState([]);
  const [assetSubtypeList, setAssetSubtypeList] = useState([]);
  const [evcsAssetList, setEvcsAssetList] = useState([]);
  const [addStatus, setAddStatus] = useState(false);
  const [popupStatus, setPopupStatus] = useState({ addAssetPopupStatus: false });
  const [defaultAssetAttribute, setDefaultAssetAttribute] = useState([]);
  const [defaultAvailableAttributeName, setDefaultAvailableAttributeName] = useState([]);
  const [copyDefaultAttribute, setCopyDefaultAttribute] = useState([]);
  const [currentAvailableAttributes, setCurrentAvailableAttributes] = useState([]);
  const [isExtendedLibrary, setIsExtendedLibrary] = useState(false);
  const [assetInfo, setAssetInfo] = useState({ manufacturerList: {} });
  const [customFleetId, setCustomFleetId] = useState(null);
  const [customAssetSubtypeId, setCustomAssetSubtypeId] = useState(null);
  const [, setProjectDetails] = useAtom(projectDetailsAtom);
  const { colorScheme } = useMantineColorScheme();
  const prevDataSourceRef = useRef(dataSource);
  const handleFleetChangeRef = useRef(null);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);


  const evcsAtom = componentDetails?.evcs;
  const evcsParams = evcsAtom?.componentAttributes?.evcsParameters;
  const [fleetTabs, setFleetTabs] = useState([]);
  const [activeFleetKey, setActiveFleetKey] = useState(null);
  const [fleetValidationErrors, setFleetValidationErrors] = useState({});
  const [isFleetSaving, setIsFleetSaving] = useState(false);
  const [dirtyFleetKeys, setDirtyFleetKeys] = useState({});
  const [pendingFleetKey, setPendingFleetKey] = useState(null);
  const [showFleetSwitchModal, setShowFleetSwitchModal] = useState(false);
  const fleetSnapshotsRef = useRef({});
  const advancedFleetBaselineRef = useRef(null);

  const loadDistributions = useCallback(async () => {
    try {
      const response = await MGA_API.getDistributions();
      const distributionData = Array.isArray(response)
        ? response
        : response?.data || response?.items || response || [];
      setDistributions(distributionData);
    } catch (error) {
      displayError("error", getErrorMessage(error));
    }
  }, []);

  const loadAssetSubtypes = useCallback(async () => {
    try {
      const response = await MGA_API.getDefaultsAssetsAttributes("EVCS");
      setAssetSubtypeList(response?.assetSubtypeList || []);
      setDefaultAssetAttribute(response?.defaultAttributeList || []);
      setDefaultAvailableAttributeName(response?.allowedAttributeName || []);
      return response;
    } catch (error) {
      displayError("error", getErrorMessage(error));
    }
  }, []);

  const callSnackbar = useCallback((type, message) => displayError(type, message), []);
  const refreshEvcsAssets = useCallback(async () => {
    const response = await MGA_API.getAssetCatalogue("EVCS");
    setEvcsAssetList(response?.assetCatalogue?.[0]?.assetList || []);
  }, []);
  const saveEvcsAsset = useCallback(async (data) => {
    try {
      const response = await MGA_API.saveAssets(data);
      await refreshEvcsAssets();
      if (customFleetId && response?.communicationModel) {
        const attributes = Object.fromEntries(
          (response.assetAttributes || response.attributes || data.attributes || []).map(
            ({ type, value: attributeValue }) => [type, attributeValue ?? ""]
          )
        );
        handleFleetChangeRef.current?.(customFleetId, {
          assetId: response.assetId,
          vehicleModelName: response.communicationModel,
          batteryCapacity: attributes.BatteryCapacity || "",
          drivingRange: attributes.DrivingRange || "",
          energyConsumption: attributes.AvgConsumption || "",
          dcChargingPower: attributes.DcFastCharging || "",
          chargingEfficiency: attributes.ChargerEfficiency || "",
        });
      }
      setPopupStatus({ addAssetPopupStatus: false });
      setAddStatus(false);
    } catch (error) {
      displayError("error", getErrorMessage(error));
    } finally {
      setLoadingComp(false);
    }
  }, [customFleetId, refreshEvcsAssets, setLoadingComp]);
  const assetManagement = useAssetManagement({
    callSnackbar,
    defaultAssetAttribute,
    defaultAvailableAttributeName,
    copyDefaultAttribute,
    setCopyDefaultAttribute,
    setCurrentAvailableAttributes,
    currentAvailableAttributes,
    isExtendedLibrary,
    addStatus,
    setAddStatus,
    setLoading: setLoadingComp,
    setPopupStatus,
    saveAssetCallback: saveEvcsAsset,
  });

  const loadEvcsAssets = useCallback(async () => {
    try {
      const response = await MGA_API.getAssetCatalogue("EVCS");
      setEvcsAssetList(response?.assetCatalogue?.[0]?.assetList || []);
    } catch (error) {
      displayError("error", getErrorMessage(error));
    }
  }, []);

  const loadEvcsAssetTypes = useCallback(async () => {
    try {
      const response = await MGA_API.getAssetType();
      const assetsType = (response || []).filter(
        (assetType) => assetType?.type?.toLowerCase() === "evcs"
      );
      assetManagement.setAssetDetails((previous) => ({ ...previous, assetsType }));
    } catch (error) {
      displayError("error", getErrorMessage(error));
    }
  }, [assetManagement.setAssetDetails]);

  useEffect(() => {
    loadDistributions();
    loadAssetSubtypes();
    loadEvcsAssets();
  }, [loadAssetSubtypes, loadDistributions, loadEvcsAssets]);

  useEffect(() => {
    loadEvcsAssetTypes();
  }, [loadEvcsAssetTypes]);

  useEffect(() => {
    setAssetInfo({
      manufacturerList: {
        EVCS: [...new Set(evcsAssetList.map((asset) => asset.manufacturer).filter(Boolean))],
      },
    });
  }, [evcsAssetList]);
  const loadStartDate =
    componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
      ?.startDate;

  // ─── Form ───────────────────────────────────────────────────────────────────
  const form = useForm({
    key: JSON.stringify(evcsAtom),
    initialValues: {
      ...evcsAtom,
      componentAttributes: {
        evcsParameters: {
          ...evcsParams,
          profileDetail: {
            ...evcsParams?.profileDetail,
            startTime: evcsParams?.profileDetail?.startTime || "00:00",
            startDate: evcsParams?.profileDetail?.startDate
              ? evcsParams.profileDetail.startDate instanceof Date
                ? evcsParams.profileDetail.startDate
                : parseISOToLocalDateAtMidnightUTC(evcsParams.profileDetail.startDate)
              : getDefaultEvcsStartDate(loadStartDate),
          },
          profileFile: evcsParams?.profileFile || [],
        },
      },
    },
    validate: {
      economicAttributes: {
        evcsParameters: {
          CAPEX: (value) =>
            value === null || String(value).trim() === "" || Number(value) === 0
              ? null
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(value)
              ? t("evcs.validation.capexRange", formatProjectNumberRange(0, 1000000000, projectDetails?.projectDTO?.numberFormat))
              : null,
          OM: (value) =>
            value === null || String(value).trim() === "" || Number(value) === 0
              ? null
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(value)
              ? t("evcs.validation.omRange", formatProjectNumberRange(0, 1000000000, projectDetails?.projectDTO?.numberFormat))
              : null,
          Replacement: (value) =>
            value === null || String(value).trim() === "" || Number(value) === 0
              ? null
              : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(value)
              ? t("evcs.validation.replacementRange", formatProjectNumberRange(0, 1000000000, projectDetails?.projectDTO?.numberFormat))
              : null,
          LifetimeYear: (value) =>
            value === null || String(value).trim() === "" || Number(value) === 0
              ? null
              : Number(value) < 0 || Number(value) > 25
              ? t("evcs.validation.lifetimeYearRange", formatProjectNumberRange(1, 25, projectDetails?.projectDTO?.numberFormat))
              : null,
        },
      },
      componentAttributes: {
        evcsParameters: {
          numberOfChargingPoints: evcsRange(
            "numberOfChargingPoints",
            1,
            9999,
            mgaRegex.oneTo9999Regex,
            true,
          ),
          chargingPointMaxPower: evcsRange(
            "chargingPointMaxPower",
            1,
            9999,
            mgaRegex.oneTo9999WithTwoDecimalRegex,
          ),
          chargingStationMaxPower: (value) => {
            const rangeError = evcsRange(
              "chargingStationMaxPower",
              1,
              1000000000,
              mgaRegex.oneToTenPower9WithTwoDecimalRegex,
            )(value);
            if (rangeError) return rangeError;

            const numberOfChargingPoints = Number(
              form.values?.componentAttributes?.evcsParameters
                ?.numberOfChargingPoints,
            );
            const chargingPointMaxPower = Number(
              form.values?.componentAttributes?.evcsParameters
                ?.chargingPointMaxPower,
            );
            const chargingStationMaxPower = Number(value);
            const minimumChargingStationMaxPower =
              numberOfChargingPoints * chargingPointMaxPower;

            return Number.isFinite(minimumChargingStationMaxPower) &&
              Number.isFinite(chargingStationMaxPower) &&
              chargingStationMaxPower < minimumChargingStationMaxPower
              ? t("evcs.validation.chargingStationMinPower", {
                  minimum: minimumChargingStationMaxPower,
                })
              : null;
          },
          fleets: (fleets) => {
            if (dataSource !== "advanced") return null;
            const selectedFleetKey = String(
              activeFleetKey ?? fleetTabs?.[0]?.key,
            );
            const selectedFleet = (fleets || []).find(
              (fleet) =>
                String(fleet.fleetKey ?? fleet.fleetId) === selectedFleetKey,
            );
            return validateFleet(selectedFleet, t) || null;
          },
          profileDetail: {
            evcsFile: (value) =>
              dataSource !== "advanced" &&
              (value && evcsParams?.profileDetail?.evcsFileClicked
                ? t("loadMethod.validation.loadFileClick")
                : evcsParams?.profileFile?.length || value
                ? null
                : t("evcs.validation.profileFileRequired")),  
            startDate: (value) => {
              const recurrence = evcsParams?.profileDetail?.recurrence;
              if (dataSource === "advanced" || recurrence === "DAILY") return null;
              return value ? null : t("loadMethod.validation.startDateRequired");
            },
            startTime: (value) => {
              const recurrence = evcsParams?.profileDetail?.recurrence;
              if (dataSource === "advanced" || recurrence === "DAILY") return null;
              return value ? null : t("loadMethod.validation.startTimeRequired");
            },
            timeResolution: (value) =>
              dataSource !== "advanced" &&
              (value ? null : t("loadMethod.validation.timeResolutionRequired")),
            units: (value) =>
              dataSource !== "advanced" &&
              (value ? null : t("loadMethod.validation.unitsRequired")),
          },
        },
      },
    },
  });

  const { setValues } = form;

  useEffect(() => {
    if (!form.isTouched()) return;
    setComponentStatus((prev) => ({
      ...prev,
      evcs: { isEditing: true, completed: false },
    }));
  }, [form.values]);

  // ─── State updater ──────────────────────────────────────────────────────────
  const updateEvcsState = useCallback(
    (category, key, value) => {
      setComponentDetails((prev) => {
        const newState = { ...prev, currentComponent: "evcs" };
        if (!key) {
          _.set(newState, `evcs.${category}`, value);
          setComponentStatus((s) => ({
            ...s,
            evcs: { isEditing: true, completed: false },
          }));
        } else {
          _.set(newState, `evcs.${category}.${key}`, value);
        }
        const detail =
          newState.evcs?.componentAttributes?.evcsParameters?.profileDetail;
        if (detail) {
          detail.startDate = detail.startDate
            ? new Date(detail.startDate)
            : getDefaultEvcsStartDate(loadStartDate);
        }
        setValues(newState?.evcs);
        return newState;
      });
    },
    [loadStartDate, setComponentDetails, setComponentStatus, setValues]
  );

  const handleFleetChange = useCallback((fleetId, field, value) => {
    const fleetKey = String(fleetId);
    if (!fleetSnapshotsRef.current[fleetKey]) {
      const currentFleet = (evcsParams?.fleets || []).find(
        (fleet) => String(fleet.fleetKey ?? fleet.fleetId) === fleetKey,
      );
      if (currentFleet) {
        fleetSnapshotsRef.current[fleetKey] = _.cloneDeep(currentFleet);
      }
    }
    setFleetValidationErrors((previous) => {
      const next = { ...previous };
      delete next[String(fleetId)];
      return next;
    });
    setComponentStatus((previous) => ({
      ...previous,
      evcs: { isEditing: true, completed: false },
    }));
    setDirtyFleetKeys((previous) => ({
      ...previous,
      [String(fleetId)]: true,
    }));
    const fleets = (evcsParams?.fleets || []).map((fleet) => {
      const fleetKey = fleet.fleetKey ?? fleet.fleetId;
      if (String(fleetKey) !== String(fleetId)) return fleet;
      const changes = typeof field === "object" ? field : { [field]: value };
      return field === "vehicleCategoryId"
        ? { ...fleet, ...changes, vehicleModelName: "" }
        : { ...fleet, ...changes };
    });

    updateEvcsState(
      "componentAttributes.evcsParameters",
      "fleets",
      fleets
    );
  }, [evcsParams?.fleets, updateEvcsState]);

  handleFleetChangeRef.current = handleFleetChange;

  const addCustomAsset = useCallback((fleetId, vehicleCategoryId) => {
    setCustomFleetId(fleetId);
    setCustomAssetSubtypeId(vehicleCategoryId);
    setAddStatus(true);
    setPopupStatus({ addAssetPopupStatus: true });
  }, []);

  const handleFleetTabsChange = useCallback((nextData, newItem) => {
    const currentFleets = evcsParams?.fleets || [];
    const fleets = newItem
      ? [
          ...currentFleets,
          {
            fleetId: -1,
            fleetKey: newItem.key,
            fleetName: newItem.label,
            vehicleModelName: "0",
            assetId: -1,
            arrivalDistribution: { tripDays: [] },
            tripDistribution: {},
          },
        ]
      : currentFleets.filter((fleet) =>
          nextData.some(
            (item) => String(item.key) === String(fleet.fleetKey ?? fleet.fleetId)
          )
        );

    if (!newItem) {
      const currentKey = activeFleetKey ?? fleetTabs?.[0]?.key;
      const currentFleetStillExists = nextData.some(
        (item) => String(item.key) === String(currentKey)
      );
      if (!currentFleetStillExists) {
        setActiveFleetKey(nextData[0]?.key ?? null);
      }
      setDirtyFleetKeys((previous) => {
        const next = { ...previous };
        const remainingKeys = new Set(nextData.map((item) => String(item.key)));
        Object.keys(next).forEach((key) => {
          if (!remainingKeys.has(key)) delete next[key];
        });
        return next;
      });
    }

    setComponentStatus((previous) => ({
      ...previous,
      evcs: { isEditing: true, completed: false },
    }));
    if (newItem) {
      fleetSnapshotsRef.current[String(newItem.key)] = _.cloneDeep(
        fleets[fleets.length - 1],
      );
      setDirtyFleetKeys((previous) => ({
        ...previous,
        [String(newItem.key)]: true,
      }));
    }
    updateEvcsState(
      "componentAttributes.evcsParameters",
      "fleets",
      fleets
    );
  }, [activeFleetKey, evcsParams?.fleets, fleetTabs, updateEvcsState]);

  const handleSaveFleet = useCallback(async (fleet) => {
    if (!fleet) {
      displayError("error", t("evcs.fleet.noSelection"));
      return false;
    }
    const errors = getFleetValidationErrors(
      fleet,
      t,
      projectDetails?.projectDTO?.numberFormat,
    );
    if (Object.keys(errors).length) {
      const fleetKey = fleet.fleetKey ?? fleet.fleetId;
      setFleetValidationErrors((previous) => ({
        ...previous,
        [String(fleetKey)]: errors,
      }));
      const probabilityAreaError = Object.entries(errors).find(([path]) =>
        path.endsWith(".probabilityArea"),
      )?.[1];
      if (probabilityAreaError) displayError("error", probabilityAreaError);
      return false;
    }
    setIsFleetSaving(true);
    try {
      const {
        fleetKey,
        arrivalDistribution,
        tripDistribution,
        ...payload
      } = fleet;
      payload.assetId = payload.assetId ?? -1;
      payload.arrivalDistribution = {
        ...arrivalDistribution,
        discretization: 1,
        arrivalProfile: toPointArray(
          getStoredOrGeneratedProfile("arrivalDistribution", arrivalDistribution),
        ),
      };
      payload.tripDistribution = {
        ...tripDistribution,
        discretization: 1,
        tripProfile: getNormalizedTripProfile(
          tripDistribution,
          fleet.drivingRange,
        ),
      };
      const response = await MGA_API.saveFleets(
        projectId,
        designId,
        designVersionId,
        payload,
      );
      const savedFleetId =
        response?.fleetId ?? response?.data?.fleetId ?? response?.fleet?.fleetId;
      if (savedFleetId !== undefined && savedFleetId !== null) {
        const fleets = (evcsParams?.fleets || []).map((item) =>
          String(item.fleetKey ?? item.fleetId) === String(fleetKey)
            ? { ...item, fleetId: savedFleetId }
            : item,
        );
        updateEvcsState(
          "componentAttributes.evcsParameters",
          "fleets",
          fleets,
        );
        const savedFleet = fleets.find(
          (item) => String(item.fleetKey ?? item.fleetId) === String(fleetKey),
        );
        if (savedFleet) {
          fleetSnapshotsRef.current[String(fleetKey)] = _.cloneDeep(savedFleet);
        }
      }
      fleetSnapshotsRef.current[String(fleetKey ?? fleet.fleetId)] = _.cloneDeep({
        ...fleet,
        ...(savedFleetId !== undefined && savedFleetId !== null
          ? { fleetId: savedFleetId }
          : {}),
      });
      setDirtyFleetKeys((previous) => {
        const next = { ...previous };
        delete next[String(fleetKey ?? fleet.fleetId)];
        return next;
      });
      displayError("success", t("evcs.fleet.saveSuccess"));
      return true;
    } catch (error) {
      displayError("error", getErrorMessage(error));
      return false;
    } finally {
      setIsFleetSaving(false);
    }
  }, [
    designId,
    designVersionId,
    evcsParams?.fleets,
    projectId,
    updateEvcsState,
  ]);

  const handleDeleteFleet = useCallback(async (fleetKeyOrItem) => {
    const fleetKey =
      typeof fleetKeyOrItem === "object"
        ? fleetKeyOrItem?.key ?? fleetKeyOrItem?.fleetKey
        : fleetKeyOrItem;
    const fleet =
      typeof fleetKeyOrItem === "object" &&
      Object.prototype.hasOwnProperty.call(fleetKeyOrItem, "fleetId")
        ? fleetKeyOrItem
        : (evcsParams?.fleets || []).find(
            (item) => String(item.fleetKey ?? item.fleetId) === String(fleetKey),
          );
    const fleetId = fleet?.fleetId;
    const isTemporaryFleet =
      fleetId === undefined ||
      fleetId === null ||
      String(fleetId).trim() === "-1" ||
      Number(fleetId) === -1;

    if (isTemporaryFleet) {
      displayError("success", t("evcs.fleet.deleteSuccess"));
      return true;
    }

    try {
      await MGA_API.deleteFleetById(fleetId);
      displayError("success", t("evcs.fleet.deleteSuccess"));
      return true;
    } catch (error) {
      displayError("error", getErrorMessage(error));
      return false;
    }
  }, [evcsParams?.fleets]);

  const handleFleetTabChange = useCallback(async (nextKey, metadata = {}) => {
    const currentKey = activeFleetKey ?? fleetTabs?.[0]?.key;
    if (metadata.reason === "delete") {
      setActiveFleetKey(nextKey ?? null);
      return;
    }
    if (
      String(nextKey) !== String(currentKey) &&
      dirtyFleetKeys[String(currentKey)]
    ) {
      setPendingFleetKey(nextKey);
      setShowFleetSwitchModal(true);
      return;
    }
    setActiveFleetKey(nextKey);
  }, [activeFleetKey, dirtyFleetKeys, evcsParams?.fleets, fleetTabs]);

  // OK: stay on the current fleet, keep its unsaved edits, don't switch.
  const handleStayOnFleet = useCallback(() => {
    if (pendingFleetKey === null) return;

    setPendingFleetKey(null);
    setShowFleetSwitchModal(false);
  }, [pendingFleetKey]);

  // Cancel: discard current fleet's unsaved edits and switch to the pending fleet.
  const handleDiscardFleetAndSwitch = useCallback(() => {
    if (pendingFleetKey === null) return;

    const currentKey = String(activeFleetKey ?? fleetTabs?.[0]?.key);
    const snapshot = fleetSnapshotsRef.current[currentKey];
    if (snapshot) {
      const fleets = (evcsParams?.fleets || []).map((fleet) =>
        String(fleet.fleetKey ?? fleet.fleetId) === currentKey
          ? _.cloneDeep(snapshot)
          : fleet
      );
      updateEvcsState("componentAttributes.evcsParameters", "fleets", fleets);
    }
    setDirtyFleetKeys((previous) => {
      const next = { ...previous };
      delete next[currentKey];
      return next;
    });
    setActiveFleetKey(pendingFleetKey);
    setPendingFleetKey(null);
    setShowFleetSwitchModal(false);
  }, [activeFleetKey, evcsParams?.fleets, fleetTabs, pendingFleetKey, updateEvcsState]);

  useEffect(() => {
    const fleets = evcsParams?.fleets || [];
    if (dataSource === "advanced" && evcsParams && !fleets.length) {
      const fleetKey = "fleet-1";
      const defaultFleet = {
        fleetId: -1,
        fleetKey,
        fleetName: "Fleet 1",
        vehicleModelName: "0",
        assetId: -1,
        arrivalDistribution: { tripDays: [] },
        tripDistribution: {},
      };
      fleetSnapshotsRef.current[fleetKey] = _.cloneDeep(defaultFleet);
      setActiveFleetKey(fleetKey);
      setDirtyFleetKeys({ [fleetKey]: true });
      updateEvcsState(
        "componentAttributes.evcsParameters",
        "fleets",
        [defaultFleet],
      );
      return;
    }

    setFleetTabs(
      fleets.map((fleet, index) => ({
        key: String(fleet.fleetKey ?? fleet.fleetId ?? `fleet-${index}`),
        fleetId: fleet.fleetId,
        fleetKey: fleet.fleetKey,
        label: fleet.fleetName || `Fleet ${index + 1}`,
        content: (
          <Fleet
            fleet={fleet}
            distributions={distributions}
            assetSubtypeList={assetSubtypeList}
            evcsAssetList={evcsAssetList}
            onFleetChange={handleFleetChange}
            onAddCustomAsset={addCustomAsset}
            validationErrors={fleetValidationErrors[String(fleet.fleetKey ?? fleet.fleetId)]}
          />
        ),
      }))
    );
  }, [
    assetSubtypeList,
    distributions,
    evcsAssetList,
    evcsParams?.fleets,
    fleetValidationErrors,
    handleFleetChange,
  ]);

  useEffect(() => {
    if (
      fleetTabs.length &&
      activeFleetKey === null
    ) {
      setActiveFleetKey(fleetTabs[0].key);
    }
  }, [activeFleetKey, fleetTabs]);

  // ─── projectDetails sync helper ─────────────────────────────────────────────
  const updateProjectDetailsWithComponentAttributes = useCallback(
    (apiResponse, dvId) => {
      setProjectDetails((prevDetails) => {
        const updatedDesignComponentsList = prevDetails?.designComponentsList?.map(
          (design) => ({
            ...design,
            designVersionComponents: design?.designVersionComponents?.map((version) => {
              if (String(version.designVersionId) !== String(dvId)) return version;
              return {
                ...version,
                componentDTOList: version?.componentDTOList?.map((component) =>
                  String(component.componentId) === String(apiResponse?.componentId)
                    ? { ...component, ...apiResponse }
                    : component
                ),
              };
            }),
          })
        );
        return { ...prevDetails, designComponentsList: updatedDesignComponentsList };
      });
    },
    []
  );

   const containerRef = useRef(null);
    const accordianRef = useRef(null);
  
    useHelpFocusBlur(accordianRef, setHelperAtom, helperAtom);
    const handleContainerClick = (event, cardId) => {
      const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
      if (interactiveTags.includes(event.target.tagName)) return;
      const cardElement = document.querySelector(`[card-id=${cardId}]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
        cardElement.style.transition = "background-color 0.5s ease";
        cardElement.style.border = "2px solid #42b4e6";
        setTimeout(() => {
          cardElement.style.border = "";
        }, 2000);
      }
    };
  
    const handleContainerKeyDown = (event, cardId) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleContainerClick(event, cardId);
      }
    };
  
    const handleBlur = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setHelperAtom({
          ...helperAtom,
          isFocused: false,
          helpSection: {
            title: "",
            description: "",
            type: "",
            other: "",
          },
        });
      }
    };
    const handleFocus = (event) => {
      const dataId = event.target.getAttribute("data-id");
      if (dataId) {
        const cardElement = document.querySelector(`[card-id="${dataId}"]`);
        if (cardElement) {
          cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
          cardElement.style.transition = "background-color 0.5s ease";
          cardElement.style.border = "2px solid #42b4e6";
          setTimeout(() => {
            cardElement.style.border = "";
          }, 2000);
        }
      }
    };
  
    const [helpMenuData] = useAtom(helpContextMenuAtom);
  
    const getFieldAttributes = (fieldKey) => {
      const field = helpMenuData?.evcs?.[fieldKey];
      return {
        title: field?.title || "",
        "data-description": field?.description || "",
        // placeholder: field?.placeholder || "",
      };
    };
  
    useEffect(() => {
      const inputElements = containerRef.current.querySelectorAll(
        "input, select, textarea, file"
      );
  
      inputElements.forEach((inputElement) => {
        inputElement.addEventListener("focus", handleFocus);
        inputElement.addEventListener("blur", handleBlur);
      });
  
      return () => {
        inputElements.forEach((inputElement) => {
          inputElement.removeEventListener("focus", handleFocus);
          inputElement.removeEventListener("blur", handleBlur);
        });
      };
    }, []);

  // ─── Save / upload ──────────────────────────────────────────────────────────
  const handleEvcsUpload = async () => {
    if (pendingFleetKey !== null) {
      return handleSaveFleet(currentFleet);
    }

    setLoadingComp(true);
    const filteredComponent = selectedDesignAndDesignVersion?.componentDTOList?.find(
      (comp) => comp.componentType === "EVCS"
    );
    const componentId = filteredComponent ? filteredComponent.componentId : -1;
    const isNewComponent = Number(componentId) === -1;
    const hasFleets = (evcsParams?.fleets || []).length > 0;
    const saveFleetsBeforeComponent = !isNewComponent && hasFleets;
    const selectedFleetKey = activeFleetKey;
    const profileDetail = evcsParams?.profileDetail || {};
    const componentEvcsParams = _.omit(evcsParams, "fleets");
    const recurrence = profileDetail.recurrence || "YEARLY";
    const isDailyRecurrence = recurrence === "DAILY";
    const payload = {
      projectId,
      designId,
      designVersionId,
      componentType: "EVCS",
      componentId,
      componentAttributes: {
        evcsParameters: {
          ...componentEvcsParams,
          profileDetail: {
            componentType: "EVCS",
            recurrence,
            profileFileName: profileDetail.profileFileName || null,
            startDate:
              dataSource === "advanced"
                ? null
                : isDailyRecurrence
                ? getUtcMidnightIso(loadStartDate)
                : toUtcISOStringWithStartTime(
                    profileDetail.startDate,
                    profileDetail.startTime
                  ),
            startTime: isDailyRecurrence ? "00:00" : (profileDetail.startTime || "00:00"),
            timeResolution: profileDetail.timeResolution || 15,
            units: profileDetail.units || "kW",
            profileDataLoc:
              dataSource === "advanced" ? profileDetail.profileDataLoc : null,
          },
          profileFile: dataSource === "advanced" ? [] : evcsParams?.profileFile || [],
        },
      },
      economicAttributes: evcsAtom?.economicAttributes || {
        evcsParameters: { CAPEX: 0, Replacement: 0, OM: 0, LifetimeYear: 0 },
      },
      datasource: dataSource === "advanced" ? "OTHER" : "Upload",
    };

    try {
      const saveFleets = async () => {
        if (dataSource !== "advanced") return true;

        const selectedFleetKey = String(activeFleetKey ?? fleetTabs?.[0]?.key);
        const fleetToSave = (evcsParams?.fleets || []).find(
          (fleet) =>
            String(fleet.fleetKey ?? fleet.fleetId) === selectedFleetKey,
        );
        const fleetsToSave = fleetToSave && dirtyFleetKeys[selectedFleetKey]
          ? [fleetToSave]
          : [];

        for (const fleet of fleetsToSave) {
          const saved = await handleSaveFleet(fleet);
          if (!saved) return false;
        }
        return true;
      };

      updateEvcsState(
        "componentAttributes.evcsParameters.profileDetail",
        "evcsFile",
        null
      );
      let response;
      if (saveFleetsBeforeComponent && !(await saveFleets())) return false;

      response = await MGA_API.importLoad(payload);

      const responseFleets =
        response?.componentAttributes?.evcsParameters?.fleets;
      if (dataSource !== "advanced" && Array.isArray(responseFleets)) {
        updateEvcsState(
          "componentAttributes.evcsParameters",
          "fleets",
          responseFleets,
        );
      } else if (dataSource !== "advanced") {
        updateEvcsState(
          "componentAttributes.evcsParameters",
          "fleets",
          [],
        );
      }

      if (!saveFleetsBeforeComponent && !(await saveFleets())) return false;

      if (selectedFleetKey !== null) {
        setActiveFleetKey(selectedFleetKey);
      }

      displayError("success", t("evcs.status.saveSuccess"));
      if (response?.componentId) {
        const res = await MGA_API.editProject(projectId);
        setProjectDetails((prev) => ({
          ...prev,
          userId: res.userId || "",
          currencyCode: res?.projectDTO?.currencyCode || "$",
          projectDTO: res.projectDTO || {},
          componentDTOList:
            flattenCountComponentDTOList(res.countComponentDTOList) || [],
          designWithVersionsDTOList: res.designWithVersionsDTOList || [],
          locationDTO: res.locationDTO || {},
          designComponentsList:
            flattenDesignCountComponent(res?.designComponentsList) || [],
        }));
        updateProjectDetailsWithComponentAttributes(response, designVersionId);
        setComponentStatus((prev) => ({
          ...prev,
          evcs: { completed: true, isEditing: false },
        }));
      }
      return true;
    } catch (error) {
      displayError('error', getErrorMessage(error));
      return false;
    } finally {
      setLoadingComp(false);
    }
  };

  const handleEvcsMethod = (event) => {
    if (dataSource === "advanced") {
      const selectedFleet = (evcsParams?.fleets || []).find(
        (fleet) =>
          String(fleet.fleetKey ?? fleet.fleetId) ===
          String(activeFleetKey ?? fleetTabs?.[0]?.key),
      );
      const selectedFleetKey = String(
        selectedFleet?.fleetKey ?? selectedFleet?.fleetId ?? activeFleetKey,
      );
      const validationErrors = getFleetValidationErrors(
        selectedFleet,
        t,
        projectDetails?.projectDTO?.numberFormat,
      );
      setFleetValidationErrors((previous) => ({
        ...previous,
        [selectedFleetKey]: validationErrors,
      }));
      if (Object.keys(validationErrors).length) {
        displayError("error", Object.values(validationErrors)[0]);
        return false;
      }
    }

    return form.onSubmit(
      () => {
        handleEvcsUpload();
      },
      (errors) => {
        const firstErrorPath = Object.keys(errors)[0];
        const inputNode = form.getInputNode(firstErrorPath);
        if (inputNode) {
          inputNode.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      },
    )(event);
  };

  useImperativeHandle(ref, () => ({ handleEvcsMethod }));

  // ─── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    setOpenSteps(evcsComponents.length > 0 || true);
  }, [evcsComponents, designVersionId]);

  useEffect(() => {
    const evcsAPIData = _.find(selectedDesignAndDesignVersion.componentDTOList, {
      componentType: "EVCS",
    });
    const datasource = evcsAPIData?.datasource?.toLowerCase();
    const isAdvanced = datasource === "other";
    setDataSource(isAdvanced ? "advanced" : initialTab || "load");
  }, [initialTab, designVersionId]);

  useEffect(() => {
    const fleets = evcsParams?.fleets || [];
    if (
      prevDataSourceRef.current !== "advanced" &&
      dataSource === "advanced" &&
      fleets.length
    ) {
      setActiveFleetKey(String(fleets[0].fleetKey ?? fleets[0].fleetId));
    }

    if (
      dataSource === "advanced" &&
      advancedFleetBaselineRef.current === null &&
      evcsParams
    ) {
      advancedFleetBaselineRef.current = _.cloneDeep(fleets);
    }

    if (prevDataSourceRef.current === "advanced" && dataSource !== "advanced") {
      const restoredFleets = advancedFleetBaselineRef.current
        ? _.cloneDeep(advancedFleetBaselineRef.current)
        : fleets.map((fleet) => {
            const fleetKey = String(fleet.fleetKey ?? fleet.fleetId);
            return fleetSnapshotsRef.current[fleetKey]
              ? _.cloneDeep(fleetSnapshotsRef.current[fleetKey])
              : fleet;
          });
      advancedFleetBaselineRef.current = null;

      updateEvcsState(
        "componentAttributes.evcsParameters",
        "fleets",
        restoredFleets,
      );
      setDirtyFleetKeys({});
      setFleetValidationErrors({});
      setPendingFleetKey(null);
      setShowFleetSwitchModal(false);
      updateEvcsState(
        "componentAttributes.evcsParameters.profileDetail",
        "profileDataLoc",
        null
      );
      updateEvcsState(
        "componentAttributes.evcsParameters",
        "profileFile",
        [],
      );
    }
    prevDataSourceRef.current = dataSource;
  }, [dataSource, evcsParams?.fleets, updateEvcsState]);

  // ─── Economic attribute updater ─────────────────────────────────────────────
  const handleEconomicChange = (key, value) => {
    setComponentDetails((prev) => ({
      ...prev,
      currentComponent: "evcs",
      evcs: {
        ...prev.evcs,
        economicAttributes: {
          evcsParameters: {
            ...prev.evcs?.economicAttributes?.evcsParameters,
            [key]: value,
          },
        },
      },
    }));
    form.setFieldValue(`economicAttributes.evcsParameters.${key}`, value);
  };

  // ─── Accordion A — method selector ──────────────────────────────────────────
  const selectedCardBorder = "2px solid #3dcd58";
  const unselectedCardBorder = "2px solid #e0e0e0";
  const selectedBoxShadow = "0 0 0 2px #3dcd5833";

  const handleTabClick = (tab) => {
    if (!tab) return;
    setDataSource(tab);
    setOpenSteps(true);
    updateEvcsState("datasource", null, tab);

    if (tab === "load" || tab === "electric") {
      updateEvcsState(
        "componentAttributes.evcsParameters",
        "profileFile",
        [],
      );
    }

    if (tab === "advanced" && evcsParams && !(evcsParams.fleets || []).length) {
      const defaultFleet = {
        fleetId: -1,
        fleetKey: "fleet-1",
        fleetName: "Fleet 1",
        vehicleModelName: "0",
        assetId: -1,
        arrivalDistribution: { tripDays: [] },
        tripDistribution: {},
      };
      fleetSnapshotsRef.current[defaultFleet.fleetKey] = _.cloneDeep(defaultFleet);
      setActiveFleetKey(defaultFleet.fleetKey);
      setDirtyFleetKeys({ [defaultFleet.fleetKey]: true });
      updateEvcsState(
        "componentAttributes.evcsParameters",
        "fleets",
        [defaultFleet],
      );
    }
  };

  const header = () => (
    <span>
      <span className="stepText col-md-1 px-0">
        {t("evcs.step", {
          number: addedStepsSeries.indexOf("evcs") + 1,
          section: "A",
        })}
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("evcs.methodSelectorTitle")}
      </span>
    </span>
  );

  const header2 = () => (
    <span>
      <span className="stepText col-md-1 px-0">
        {t("evcs.step", {
          number: addedStepsSeries.indexOf("evcs") + 1,
          section: "C",
        })}
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("evcs.investmentCharges")}
      </span>
    </span>
  );

  const children2 = () => (
    <div       
     ref={accordianRef}
      onClick={(e) => handleContainerClick(e, "accordion-evcs-2")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-evcs-2")}
      data-id="accordion-evcs-2"
      role="button"
      tabIndex={0}
      >
    <Grid gutter="md" className="mt-2 mb-2" >
      <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          {...getFieldAttributes("field-10")}
          data-id="field-10"
          label={t("evcs.capex.label")}
          placeholder={t("evcs.capex.placeholder")}
          {...form.getInputProps("economicAttributes.evcsParameters.CAPEX")}
          disabled
          onChange={(e) => {
            const value = formatInputValue(e.target.value);
            handleEconomicChange("CAPEX", value);
          }}
          rightSection={<div style={{ padding: "4px" }}>{projectDetails?.projectDTO?.currencyCode || "$"}/kW</div>}
          rightSectionProps={{ style: { pointerEvents: "none" } }}
          rightSectionWidth={60}
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          {...getFieldAttributes("field-11")}
          data-id="field-11"
          label={t("evcs.om.label")}
          placeholder={t("evcs.om.placeholder")}
          {...form.getInputProps("economicAttributes.evcsParameters.OM")}
          disabled
          onChange={(e) => {
            const value = formatInputValue(e.target.value);
            handleEconomicChange("OM", value);
          }}
          rightSection={<div style={{ padding: "4px" }}>{projectDetails?.projectDTO?.currencyCode || "$"}/kW</div>}
          rightSectionProps={{ style: { pointerEvents: "none" } }}
          rightSectionWidth={60}
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          {...getFieldAttributes("field-12")}
          data-id="field-12"
          label={t("evcs.replacement.label")}
          placeholder={t("evcs.replacement.placeholder")}
          {...form.getInputProps("economicAttributes.evcsParameters.Replacement")}
          disabled
          onChange={(e) => {
            const value = formatInputValue(e.target.value);
            handleEconomicChange("Replacement", value);
          }}
          rightSection={<div style={{ padding: "4px" }}>{projectDetails?.projectDTO?.currencyCode || "$"}/kW</div>}
          rightSectionProps={{ style: { pointerEvents: "none" } }}
          rightSectionWidth={60}
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          {...getFieldAttributes("field-13")}
          data-id="field-13"
          label={t("evcs.lifeTimeYears.label")}
          placeholder={t("evcs.lifeTimeYears.placeholder")}
          {...form.getInputProps("economicAttributes.evcsParameters.LifetimeYear")}
          disabled
          onChange={(e) => {
            handleEconomicChange("LifetimeYear", e.currentTarget.value.trim());
          }}
          rightSection={<div style={{ padding: "4px", marginRight: "1rem" }}>{t("evcs.lifeTimeYears.unit")}</div>}
          rightSectionProps={{ style: { pointerEvents: "none" } }}
        />
      </Grid.Col>
    </Grid>
    </div>
  );

  const children = () => (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "24px",
        marginBottom: "24px",
        marginTop: "24px",
      }}
      ref={accordianRef}
      onClick={(e) => handleContainerClick(e, "accordion-evcs-1")}
      onKeyDown={(e) => handleContainerKeyDown(e, "accordion-evcs-1")}
      data-id="accordion-evcs-1"
      role="button"
      tabIndex={0}
    >
      <Card
        data-id="field-1"
        shadow="sm"
        padding="xs"
        radius="md"
        onClick={() => {
          handleTabClick("load");
         if (containerRef.current) {
                const cardElement = document.querySelector(
                  '[card-id="field-1"]'
                );
                if (cardElement) {
                  cardElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  cardElement.style.transition = "background-color 0.5s ease";
                  cardElement.style.border = "2px solid #42b4e6";
                  setTimeout(() => {
                    cardElement.style.border = "";
                  }, 2000);
                }
              }
            }}
        style={{
          flex: "0 1 500px",
          minWidth: "180px",
          maxWidth: "220px",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border:
            dataSource === "load" || dataSource === "electric"
              ? selectedCardBorder
              : unselectedCardBorder,
          boxShadow:
            dataSource === "load" || dataSource === "electric"
              ? selectedBoxShadow
              : undefined,
          transition: "border 0.2s, box-shadow 0.2s",
          textAlign: "center",
          padding: "14px 12px",
        }}
      >
        <Text
          align="center"
          weight={600}
          size="xs"
          className="loadSubHeading mt-1 mb-1"
          style={{ whiteSpace: "normal", fontSize: "16px", paddingBottom: "4px" }}
        >
          {t("evcs.importerTitle")}
        </Text>
        <Text
          align="center"
          size="xs"
          className="loadText mb-1"
          style={{ whiteSpace: "normal", fontSize: "12px" }}
        >
          {t("evcs.importerDescription")}
        </Text>
      </Card>

      <Card
        data-id="field-2"
        shadow="sm"
        padding="xs"
        radius="md"
        style={{
          flex: "0 1 500px",
          minWidth: "180px",
          maxWidth: "220px",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border:
            dataSource === "advanced"
              ? selectedCardBorder
              : unselectedCardBorder,
          boxShadow:
            dataSource === "advanced" ? selectedBoxShadow : undefined,
          transition: "border 0.2s, box-shadow 0.2s",
          textAlign: "center",
          padding: "14px 12px",
          opacity: 1,
        }}
         onClick={() => {
            handleTabClick("advanced");
         if (containerRef.current) {
                const cardElement = document.querySelector(
                  '[card-id="field-2"]'
                );
                if (cardElement) {
                  cardElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  cardElement.style.transition = "background-color 0.5s ease";
                  cardElement.style.border = "2px solid #42b4e6";
                  setTimeout(() => {
                    cardElement.style.border = "";
                  }, 2000);
                }
              }
            }}
      >
        <Text
          align="center"
          weight={600}
          size="xs"
          className="loadSubHeading mt-1 mb-1"
          style={{ whiteSpace: "normal", fontSize: "16px", paddingBottom: "4px" }}
        >
          {t("evcs.advancedTitle")}
        </Text>
        <Text
          align="center"
          size="xs"
          className="loadText mb-1"
          style={{ whiteSpace: "normal", fontSize: "12px" }}
        >
          {t("importAshry")}
        </Text>
      </Card>
    </div>
  );

  const header4 = () => (
    <span>
      <span className="stepText col-md-1 px-0">
        {t("evcs.step", {
          number: addedStepsSeries.indexOf("evcs") + 1,
          section: "B",
        })}
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("evcs.fleet.configure")}
      </span>
    </span>
  );

  const fleets = evcsParams?.fleets || [];
  const children4 = () => (
    <div
      ref={accordianRef}
      style={{ margin: "24px 0" }}
    >
      <FleetTabs
        data={fleetTabs}
        allowAdd={selectedVersion?.versionStatus !== "COMPLETED" && selectedVersion?.versionStatus !== "SIMULATED"}
        activeKey={activeFleetKey ?? fleetTabs?.[0]?.key}
        onChange={handleFleetTabChange}
        onDataChange={handleFleetTabsChange}
        onDeleteFleet={handleDeleteFleet}
        dirtyFleetKeys={dirtyFleetKeys}
        onBeforeAdd={(item) =>
          handleSaveFleet(
            fleets.find(
              (fleet) =>
                String(fleet.fleetKey ?? fleet.fleetId) === String(item?.key),
            ),
          )
        }
        createItem={({ key, label }) => ({
          key,
          label,
          fleetId: -1,
          fleetKey: key,
          content: (
            <Fleet
              fleet={{ fleetId: -1, fleetKey: key, fleetName: label, vehicleModelName: "0" }}
              distributions={distributions}
              assetSubtypeList={assetSubtypeList}
              evcsAssetList={evcsAssetList}
              onFleetChange={handleFleetChange}
              onAddCustomAsset={addCustomAsset}
              validationErrors={fleetValidationErrors[String(key)]}
            />
          ),
        })}
      />
    </div>
  );

  const header5 = () => (
    <span>
      <span className="stepText col-md-1 px-0">
        {t("evcs.step", {
          number: addedStepsSeries.indexOf("evcs") + 1,
          section: "C",
        })}
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t("evcs.power.configure")}
      </span>
    </span>
  );

  const children5 = () => (
    <div ref={accordianRef}>
    <Grid gutter="md" className="mt-2 mb-2" >
      <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          label={t("evcs.power.numberOfChargingPoints")}
          {...form.getInputProps("componentAttributes.evcsParameters.numberOfChargingPoints")}
          value={form.values?.componentAttributes?.evcsParameters?.numberOfChargingPoints || ""}
          onChange={(e) => {
            const value = formatInputValue(e.target.value);
            updateEvcsState(
              "componentAttributes.evcsParameters",
              "numberOfChargingPoints",
              value
            );
          }}
          required
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          label={t("evcs.power.chargingPointMaxPower")}
          {...form.getInputProps("componentAttributes.evcsParameters.chargingPointMaxPower")}
          value={form.values?.componentAttributes?.evcsParameters?.chargingPointMaxPower || ""}
          onChange={(e) => {
            const value = formatInputValue(e.target.value);
            updateEvcsState(
              "componentAttributes.evcsParameters",
              "chargingPointMaxPower",
              value
            );
          }}
          required
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          label={t("evcs.power.chargingStationMaxPower")}
          min={
            Number(form.values?.componentAttributes?.evcsParameters?.numberOfChargingPoints || 0) *
            Number(form.values?.componentAttributes?.evcsParameters?.chargingPointMaxPower || 0)
          }
          {...form.getInputProps("componentAttributes.evcsParameters.chargingStationMaxPower")}
          value={form.values?.componentAttributes?.evcsParameters?.chargingStationMaxPower || ""}
          onChange={(e) => {
            const value = formatInputValue(e.target.value);
            updateEvcsState(
              "componentAttributes.evcsParameters",
              "chargingStationMaxPower",
              value
            );
          }}
          required
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
          <Select
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("evcs.power.chargingStrategy")}
            value={form.values?.componentAttributes?.evcsParameters?.chargingStrategy || "ADVANCED_CONTROL_SOLUTION"}
            data={[
              {value:"ADVANCED_CONTROL_SOLUTION",label:t("evcs.power.strategy.advancedControl")},
              {value:"TOU_AND_PV_RECOVERY_ONLY",label:t("evcs.power.strategy.touAndPvRecovery")},
              {value:"PV_RECOVERY_ONLY",label:t("evcs.power.strategy.pvRecovery")},
              {value:"POWER_LIMIT_ONLY",label:t("evcs.power.strategy.powerLimit")},
              {value:"UNCONTROLLED_CHARGING",label:t("evcs.power.strategy.uncontrolled")}
            ]}
            {...form.getInputProps(
              "componentAttributes.evcsParameters.chargingStrategy"
            )}
            onChange={(value) => {
              updateEvcsState(
                "componentAttributes.evcsParameters",
                "chargingStrategy",
                value
              );
            }}
            required
            size="sm"
            withinPortal={true}
            searchable
          />
      </Grid.Col>
    </Grid>
    </div>
  );

  const assetEditor = popupStatus.addAssetPopupStatus
    ? createPortal(
        <AddAsset
          getDefaultAssetsAttributeList={loadAssetSubtypes}
          addAssetPopupStatus={popupStatus.addAssetPopupStatus}
          addStatus={addStatus}
          isBessSeletedDefault
          assetsType={assetManagement.assetDetails?.assetsType}
          assetAttributes={assetManagement.assetDetails?.assetAttributes}
          assetDetailsData={assetManagement.assetDetails}
          manufacturerList={assetInfo.manufacturerList}
          updatedAsset={assetManagement.updatedAsset}
          onCloseAssetPopUp={assetManagement.onCloseAssetPopUp}
          onUpdateAsset={assetManagement.onUpdateAsset}
          getAssetDetailsAttribute={assetManagement.getAssetDetailsAttribute}
          addAttributeToLibrary={assetManagement.addAttributeToLibrary}
          handleAsset={assetManagement.handleAsset}
          handleAssetSelection={assetManagement.handleAssetSelection}
          asset={assetManagement.asset}
          projectTypes={[]}
          handleEditAsset={assetManagement.handleEditAsset}
          handleDeleteAsset={assetManagement.handleDeleteAsset}
          handleAssetAttribute={assetManagement.handleAssetAttribute}
          handleOkEditAsset={assetManagement.handleOkEditAsset}
          handleCancelUpdateAsset={assetManagement.handleCancelUpdateAsset}
          setIsExtendedLibrary={setIsExtendedLibrary}
          isExtendedLibrary={isExtendedLibrary}
          inputRefs={assetManagement.inputRefs}
          currentAvailableAttributes={currentAvailableAttributes}
          defaultAssetAttribute={defaultAssetAttribute}
          setAssetDetailsEx={assetManagement.setAssetDetails}
          setCurrentAvailableAttributes={setCurrentAvailableAttributes}
          isAssetTypeDisabled
          assetSubtypeList={assetSubtypeList}
          selectedAssetSubtypeId={customAssetSubtypeId}
          isPortaled
        />,
        document.body
      )
    : null;

  const currentFleet = fleets.find(
    (fleet) =>
      String(fleet.fleetKey ?? fleet.fleetId) ===
      String(activeFleetKey ?? fleetTabs?.[0]?.key),
  );
  const pendingFleet = fleets.find(
    (fleet) =>
      String(fleet.fleetKey ?? fleet.fleetId) === String(pendingFleetKey),
  );

  // Save: save the current fleet's changes and stay on it (no switch).
  const handleSaveAndStayOnFleet = useCallback(async () => {
    if (!currentFleet) return;

    await handleSaveFleet(currentFleet);
    setPendingFleetKey(null);
    setShowFleetSwitchModal(false);
  }, [currentFleet, handleSaveFleet]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="row" ref={containerRef}>
      <div className="container-fluid">
        <AccordionItem id={1} header={header()} key={1} children={children()} />
        {dataSource === "advanced" ? (
          <>
          <AccordionItem id={4} header={header4()} key={4} children={children4()} />
          <AccordionItem id={5} header={header5()} key={5} children={children5()} />
          </>
        ) :
        (<>
        <EVCSUploadMethod
          form={form}
          evcsParameters={evcsParams}
          loadStartDate={
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail?.startDate
          }
          updateEvcsState={updateEvcsState}
          openSteps={openSteps}
          projectDetails={projectDetails}
          setLoadingComp={setLoadingComp}
          getFieldAttributes={getFieldAttributes}
        />

        <AccordionItem id={3} header={header2()} key={3} children={children2()} />
        </>)}
      </div>
      {assetEditor}
      <SEConfirmModal
        opened={showFleetSwitchModal}
        onClose={handleDiscardFleetAndSwitch}
        onConfirm={handleStayOnFleet}
        confirmLabel={t("common.ok")}
        cancelLabel={t("evcs.fleet.cancel")}
        header={"Warning"}
        confirmSeColor="standard"
        message={
          <Stack spacing={4}>
            <Text>
              {t("evcs.fleet.switchConfirmation", {
                currentFleet: currentFleet?.fleetName || t("evcs.fleet.thisFleet"),
                pendingFleet: pendingFleet?.fleetName || t("evcs.fleet.selectedFleet"),
              })}
            </Text>
            <Text>{t("evcs.fleet.okHint")}</Text>
            <Text>{t("evcs.fleet.cancelHint")}</Text>
            <Text>{t("evcs.fleet.saveHint")}</Text>
          </Stack>
        }
        onExtra={handleSaveAndStayOnFleet}
        extraLabel={t("common.save")}
        extraLoading={isFleetSaving}
        extraLoadingLabel={t("common.saving")}
      />
    </div>
  );
});

EVCSMethod.displayName = "EVCSMethod";

export default EVCSMethod;
